import type { StickyNote } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { addPristineId } from './stickyNotes.slice';
import type {
  CreateStickyNoteBody,
  DeleteStickyNoteArgs,
  StickyNoteTarget,
  UpdateStickyNoteBody,
} from './stickyNotes.types';
import { baseQueryWithAuth } from '../baseQuery';

const toTarget = ({ target_type, target_id }: StickyNoteTarget): StickyNoteTarget => ({
  target_type,
  target_id,
});

export const stickyNotesApi = createApi({
  reducerPath: 'stickyNotesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['StickyNotes'],
  endpoints: (build) => ({
    getStickyNotes: build.query<StickyNote[], StickyNoteTarget>({
      query: (target) => ({ url: 'sticky-notes', params: toTarget(target) }),
      providesTags: (_result, _error, { target_type, target_id }) => [
        { type: 'StickyNotes', id: `${target_type}:${target_id}` },
      ],
    }),
    createStickyNote: build.mutation<StickyNote, CreateStickyNoteBody>({
      query: (body) => ({ url: 'sticky-notes', method: 'POST', body }),
      // A blank note must be marked pristine before it enters the cache.
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;

          if (!created.text) dispatch(addPristineId(created.id));

          dispatch(
            stickyNotesApi.util.updateQueryData('getStickyNotes', toTarget(body), (draft) => {
              draft.push(created);
            }),
          );
        } catch (error) {
          console.error('Failed to create sticky note:', error);
        }
      },
    }),
    updateStickyNote: build.mutation<StickyNote, UpdateStickyNoteBody>({
      // The target only locates the cache entry; it is not sent.
      query: ({ id, text, color, position_x, position_y }) => ({
        url: `sticky-notes/${id}`,
        method: 'PATCH',
        body: { text, color, position_x, position_y },
      }),
      async onQueryStarted({ id, target_type, target_id, ...patch }, { dispatch, queryFulfilled }) {
        const target = { target_type, target_id };
        const optimistic = dispatch(
          stickyNotesApi.util.updateQueryData('getStickyNotes', target, (draft) => {
            const note = draft.find((candidate) => candidate.id === id);

            if (note) Object.assign(note, patch);
          }),
        );

        try {
          const { data: saved } = await queryFulfilled;

          dispatch(
            stickyNotesApi.util.updateQueryData('getStickyNotes', target, (draft) => {
              const index = draft.findIndex((candidate) => candidate.id === id);

              if (index !== -1) draft[index] = saved;
            }),
          );
        } catch (error) {
          optimistic.undo();
          console.error('Failed to update sticky note:', id, error);
        }
      },
    }),
    deleteStickyNote: build.mutation<unknown, DeleteStickyNoteArgs>({
      query: ({ id }) => ({ url: `sticky-notes/${id}`, method: 'DELETE' }),
      // Not optimistic: the note leaves the cache only once the server confirms.
      async onQueryStarted({ id, ...target }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            stickyNotesApi.util.updateQueryData('getStickyNotes', toTarget(target), (draft) =>
              draft.filter((candidate) => candidate.id !== id),
            ),
          );
        } catch (error) {
          console.error('Failed to delete sticky note:', id, error);
        }
      },
    }),
  }),
});

export const {
  useGetStickyNotesQuery,
  useCreateStickyNoteMutation,
  useUpdateStickyNoteMutation,
  useDeleteStickyNoteMutation,
} = stickyNotesApi;
